export const getCustomerSupplierGroupKey = (
  customerId: string,
  supplierId?: string,
) => `${customerId}__${supplierId || "no-supplier"}`;

export const getConsolidatedSupplierGroupKey = (supplierId?: string) =>
  `supplier__${supplierId || "no-supplier"}`;

type SelectableGroup = {
  selectedOrders: string[];
  supplierId?: string;
  isConsolidatedGroup?: boolean;
  consigneeNames?: Record<string, string>;
};

const getConsigneeCount = (group: SelectableGroup): number =>
  new Set(Object.values(group.consigneeNames || {})).size;

export const isConsolidatedSelectionGroup = (
  groupKey: string,
  group: Pick<SelectableGroup, "isConsolidatedGroup">,
): boolean =>
  Boolean(group.isConsolidatedGroup) || groupKey.startsWith("supplier__");

export const stripConsolidatedSelectionGroups = <T extends SelectableGroup>(
  selectionsByGroup: Record<string, T>,
): Record<string, T> => {
  const next: Record<string, T> = {};

  for (const [groupKey, group] of Object.entries(selectionsByGroup)) {
    if (!isConsolidatedSelectionGroup(groupKey, group)) {
      next[groupKey] = group;
    }
  }

  return next;
};

export const getActiveSelectionGroups = <T extends SelectableGroup>(
  selectionsByGroup: Record<string, T>,
  allowMultiCustomerShipment = false,
): Array<[string, T]> =>
  Object.entries(selectionsByGroup).filter(([groupKey, group]) => {
    if (group.selectedOrders.length === 0) {
      return false;
    }

    const isConsolidated = isConsolidatedSelectionGroup(groupKey, group);

    if (!allowMultiCustomerShipment) {
      return !isConsolidated;
    }

    if (isConsolidated) {
      return getConsigneeCount(group) > 1;
    }

    const consolidatedKey = getConsolidatedSupplierGroupKey(group.supplierId);
    const consolidatedGroup = selectionsByGroup[consolidatedKey];

    if (consolidatedGroup && getConsigneeCount(consolidatedGroup) > 1) {
      return false;
    }

    return true;
  });
